import { useCallback, useContext, useState } from "react";

import { createError, parseGraphQLError } from "utils/errors";

import { ToasterContext } from "scenes/Hubly/components/Workspace/Provider";

import { createResolverFactory, useMutation } from ".";

function mutationFactory() {
  const query = `
      mutation updateInsightLicense($userId: ID!) {
        updateInsightLicense(userId: $userId, enableInsights: true) {
          eligibility {
            cause
            eligible
          }
        }
      }`;

  const resolver = ({ data: { updateInsightLicense } }) => {
    return { ...updateInsightLicense };
  };

  return { query: query, resolver: createResolverFactory(resolver) };
}

function useEligibilityChecker({ id, insightsEnabled } = {}) {
  const mutate = useMutation(mutationFactory);
  const [error, setError] = useState();
  const [isProcessing, setProcessing] = useState();
  const { fireError } = useContext(ToasterContext);

  const raiseError = useCallback((e) => {
    setError(e);
    return false;
  }, []);

  return {
    error: error,
    isProcessing: isProcessing,
    checkInsights: useCallback(async () => {
      if (insightsEnabled) return true;

      try {
        setError(null);
        setProcessing(true);

        const resp = await mutate({ userId: id });
        if (resp?.eligibility?.eligible) return true;

        return raiseError({
          type: "ELIGIBILITY",
          message: resp?.eligibility?.cause,
        });
      } catch (e) {
        const errorObj = createError({
          ...parseGraphQLError(e),
          name: "EligibilityChecker",
          defaultMessage:
            "Failed to check for user's eligibility. Please reach out to our support team for further assistance",
        });

        if (
          errorObj.message ===
          "Please ask one of your hub administrators to enable your insights feature via the account settings page."
        )
          return raiseError({ type: "PERMISSION", message: errorObj.message });

        fireError(errorObj.message, errorObj);
        return raiseError({ type: "OTHER_ERROR", message: errorObj.message });
      } finally {
        setProcessing(false);
      }
    }, [id, insightsEnabled, mutate, fireError, raiseError]),
  };
}

export default useEligibilityChecker;
